import { HttpResponse, HttpError, RequestOptions } from '@/lib/http-client/types';
import { buildUrl, getDefaultHeaders, mergeHeaders, createTimeoutSignal } from '@/lib/http-client/utils';

export class HttpClient {
    private baseUrl: string;
    private defaultHeaders: Record<string, string>;
    private dynamicHeaderProviders: Record<string, () => string | null | undefined> = {};
    private defaultTimeout: number;
    private onUnauthorized: () => void;

    constructor(baseUrl: string = '', defaultHeaders: Record<string, string> = {}, defaultTimeout: number = 30000, onUnauthorized: () => void = () => { }) {
        this.baseUrl = baseUrl;
        this.defaultHeaders = { ...getDefaultHeaders(), ...defaultHeaders };
        this.defaultTimeout = defaultTimeout;
        this.onUnauthorized = onUnauthorized;
    }

    private async request<T>(
        method: string,
        path: string,
        options: RequestOptions = {}
    ): Promise<HttpResponse<T>> {
        const {
            headers = {},
            params,
            body,
            timeout = this.defaultTimeout,
            signal,
            keepalive,
        } = options;

        const url = buildUrl(this.baseUrl, path, params);
        const mergedHeaders = mergeHeaders(this.defaultHeaders, this.resolveDynamicHeaders(), headers);

        const requestInit: RequestInit = {
            method,
            headers: mergedHeaders,
            signal: signal || (timeout ? createTimeoutSignal(timeout) : undefined),
            ...(keepalive !== undefined && { keepalive }),
        };

        if (body !== undefined) {
            if (body instanceof FormData) {
                delete (requestInit.headers as Record<string, string>)['Content-Type'];
                requestInit.body = body;
            } else {
                requestInit.body = JSON.stringify(body);
            }
        }

        try {
            const response = await fetch(url, {
                ...requestInit,
                redirect: 'follow',
            });


            if (response.status >= 300 && response.status < 400 && response.redirected) {
                return {
                    data: { redirectUrl: response.url } as unknown as T,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                };
            }

            if (response.status === 401 || response.status === 403) {
                this.onUnauthorized();
                return {
                    data: undefined as unknown as T,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                };
            }

            if (!response.ok) {
                const error: HttpError = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                error.status = response.status;
                error.statusText = response.statusText;

                const clonedResponse = response.clone();
                const contentType = clonedResponse.headers.get('content-type');

                try {
                    if (contentType?.includes('application/json')) {
                        error.data = await clonedResponse.json();
                    } else {
                        error.data = await clonedResponse.text();
                    }
                } catch {
                    error.data = null;
                }

                throw error;
            }

            const contentType = response.headers.get('content-type');
            let data: T;

            if (contentType?.includes('application/json')) {
                data = await response.json();
            } else {
                data = (await response.text()) as unknown as T;
            }

            return {
                data,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
            };
        } catch (error) {
            if (error instanceof Error && 'status' in error) {
                throw error;
            }

            const httpError: HttpError = new Error(error instanceof Error ? error.message : 'Network error');
            httpError.status = 0;
            throw httpError;
        }
    }

    async get<T>(path: string, options: RequestOptions = {}): Promise<HttpResponse<T>> {
        return this.request<T>('GET', path, options);
    }

    async post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<HttpResponse<T>> {
        return this.request<T>('POST', path, { ...options, body });
    }

    async put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<HttpResponse<T>> {
        return this.request<T>('PUT', path, { ...options, body });
    }

    async patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<HttpResponse<T>> {
        return this.request<T>('PATCH', path, { ...options, body });
    }

    async delete<T>(path: string, options: RequestOptions = {}): Promise<HttpResponse<T>> {
        return this.request<T>('DELETE', path, options);
    }

    setBaseUrl(baseUrl: string): void {
        this.baseUrl = baseUrl;
    }

    setDefaultHeader(key: string, value: string): void {
        this.defaultHeaders[key] = value;
    }

    // Resolved on every request so headers whose value can change over time
    // (e.g. x-session-id after a session reset) are never stale.
    setDynamicHeader(key: string, provider: () => string | null | undefined): void {
        this.dynamicHeaderProviders[key] = provider;
    }

    private resolveDynamicHeaders(): Record<string, string> {
        const headers: Record<string, string> = {};
        for (const [key, provider] of Object.entries(this.dynamicHeaderProviders)) {
            try {
                const value = provider();
                if (value) {
                    headers[key] = value;
                }
            } catch {
                // A failing provider must not fail the request itself.
            }
        }
        return headers;
    }

    removeDefaultHeader(key: string): void {
        delete this.defaultHeaders[key];
    }

    setDefaultTimeout(timeout: number): void {
        this.defaultTimeout = timeout;
    }
}

